import React, { useState } from 'react';
import api from '../core/api';

interface LoginProps {
    onLogin: (orgId: string, role?: string) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
    const [isRegisterMode, setIsRegisterMode] = useState(false);
    const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [credentials, setCredentials] = useState({
        email: '',
        password: '',
        name: '',
        businessName: '',
        phone: ''
    });
    const [resetEmail, setResetEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [twoFactorPending, setTwoFactorPending] = useState(false);
    const [tempToken, setTempToken] = useState('');
    const [totpCode, setTotpCode] = useState('');

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const response = await api.post('/auth/login', {
                email: credentials.email,
                password: credentials.password
            });

            // 2FA required
            if (response.data.requires2FA) {
                setTempToken(response.data.tempToken);
                setTwoFactorPending(true);
                return;
            }

            const { token, user, organization } = response.data;
            localStorage.setItem('auth-token', token);
            localStorage.setItem('user-data', JSON.stringify(user));

            if (organization?.id) {
                localStorage.setItem('org-id', organization.id);
            }
            // Super admin has no organization — still log them in
            onLogin(organization?.id || null, user?.role);
        } catch (err: any) {
            console.error('Login error:', err);
            setError(err.response?.data?.error || 'Error al iniciar sesión. Verifique sus credenciales.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleTotpVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            const response = await api.post('/auth/2fa/login', { tempToken, code: totpCode });
            const { token, user, organization } = response.data;
            localStorage.setItem('auth-token', token);
            localStorage.setItem('user-data', JSON.stringify(user));
            if (organization?.id) {
                localStorage.setItem('org-id', organization.id);
            }
            onLogin(organization?.id || null, user?.role);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Código incorrecto. Intenta nuevamente.');
            setTotpCode('');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccess('');

        if (credentials.password.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            setIsLoading(false);
            return;
        }

        try {
            await api.post('/auth/register', {
                email: credentials.email,
                password: credentials.password,
                name: credentials.name,
                businessName: credentials.businessName,
                phone: credentials.phone
            });

            setSuccess('¡Cuenta creada exitosamente! Ahora puedes iniciar sesión.');
            setIsRegisterMode(false);
            setCredentials({ ...credentials, password: '' });
        } catch (err: any) {
            console.error('Register error:', err);
            setError(err.response?.data?.error || 'Error al crear cuenta. Intente nuevamente.');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleMode = () => {
        setIsRegisterMode(!isRegisterMode);
        setIsForgotPasswordMode(false);
        setError('');
        setSuccess('');
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            await api.post('/auth/forgot-password', { email: resetEmail });
            setSuccess('Si el correo existe, recibirás instrucciones para restablecer tu contraseña.');
            setResetEmail('');
        } catch (err: any) {
            console.error('Forgot password error:', err);
            // Don't reveal if email exists or not for security
            setSuccess('Si el correo existe, recibirás instrucciones para restablecer tu contraseña.');
        } finally {
            setIsLoading(false);
        }
    };

    const showForgotPassword = () => {
        setIsForgotPasswordMode(true);
        setIsRegisterMode(false);
        setError('');
        setSuccess('');
    };

    const backToLogin = () => {
        setIsForgotPasswordMode(false);
        setError('');
        setSuccess('');
    };

    // ── 2FA verification screen ──────────────────────────────────────────
    if (twoFactorPending) return (
        <div className="dark relative flex min-h-screen w-full flex-col items-center justify-center px-0 py-6 sm:p-4 overflow-hidden bg-background-dark">
            <div className="aurora-bg" aria-hidden="true">
                <div className="aurora-blob"></div>
                <div className="aurora-blob"></div>
                <div className="aurora-blob"></div>
            </div>
            <div className="relative w-full max-w-[420px] mx-3 sm:mx-auto rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl shadow-2xl p-6 sm:p-8 flex flex-col gap-5 z-10">
                <div className="flex flex-col items-center text-center gap-2">
                    <div className="w-14 h-14 rounded-full bg-primary/20 border border-white/5 flex items-center justify-center mb-2">
                        <span className="material-symbols-outlined text-primary text-[28px]">lock</span>
                    </div>
                    <h2 className="text-2xl font-bold text-white">Verificación 2FA</h2>
                    <p className="text-slate-400 text-sm">Ingresa el código de 6 dígitos de tu aplicación autenticadora</p>
                </div>
                <form onSubmit={handleTotpVerify} className="flex flex-col gap-4">
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        placeholder="000000"
                        value={totpCode}
                        onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                        className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-white/10 text-white text-center text-2xl tracking-[0.5em] placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50"
                        autoFocus
                    />
                    {error && <p className="text-brand-danger text-sm text-center">{error}</p>}
                    <button
                        type="submit"
                        disabled={isLoading || totpCode.length !== 6}
                        className="w-full py-3 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-semibold transition-colors"
                    >
                        {isLoading ? 'Verificando...' : 'Verificar'}
                    </button>
                    <button type="button" onClick={() => { setTwoFactorPending(false); setError(''); }} className="text-slate-500 text-sm hover:text-slate-300 text-center">
                        ← Volver al inicio de sesión
                    </button>
                </form>
            </div>
        </div>
    );

    return (
        <div className="dark relative flex min-h-screen w-full flex-col items-center justify-center p-4 overflow-hidden bg-background-dark">
            {/* Ambient Background Effects */}
            <div className="aurora-bg" aria-hidden="true">
                <div className="aurora-blob"></div>
                <div className="aurora-blob"></div>
                <div className="aurora-blob"></div>
            </div>

            {/* Glassmorphism Card */}
            <div className="relative w-full max-w-[400px] rounded-xl border border-white/10 bg-slate-900/60 backdrop-blur-xl shadow-2xl p-8 flex flex-col gap-6 z-10">
                {/* Branding / Header */}
                <div className="flex flex-col items-center text-center gap-2">
                    <img src="/logo.png" alt="Diana IA" className="h-12 sm:h-16 w-auto mb-1 sm:mb-2 drop-shadow-lg" />
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">Diana IA</h2>
                    <p className="text-slate-400 text-sm font-normal">
                        {isForgotPasswordMode
                            ? 'Ingresa tu correo para recuperar acceso'
                            : isRegisterMode
                                ? 'Crea tu cuenta para comenzar'
                                : 'Hola, qué bueno verte por aquí'}
                    </p>
                </div>

                {/* Forgot Password Form */}
                {isForgotPasswordMode ? (
                    <form onSubmit={handleForgotPassword} className="flex flex-col gap-4 w-full">
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-slate-300 uppercase tracking-wide ml-1" htmlFor="resetEmail">
                                Correo Electrónico
                            </label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-primary transition-colors">
                                    <span className="material-symbols-outlined text-[20px]">mail</span>
                                </div>
                                <input
                                    type="email"
                                    id="resetEmail"
                                    placeholder="tu@correo.com"
                                    value={resetEmail}
                                    onChange={e => setResetEmail(e.target.value)}
                                    className="block w-full rounded-lg border-0 bg-background-dark/80 py-3.5 pl-11 pr-4 text-white shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-slate-500 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm transition-all duration-200"
                                    required
                                />
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-brand-danger/10 border border-brand-danger/20 text-brand-danger text-sm">
                                <span className="material-symbols-outlined text-[18px]">error</span>
                                {error}
                            </div>
                        )}

                        {/* Success Message */}
                        {success && (
                            <div className="flex items-center gap-2 p-3 rounded-lg bg-brand-accent/10 border border-brand-accent/20 text-brand-accent text-sm">
                                <span className="material-symbols-outlined text-[18px]">check_circle</span>
                                {success}
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="group relative flex w-full justify-center rounded-xl bg-gradient-to-r from-primary to-primary-light p-[1px] shadow-lg shadow-primary/25 transition-all active:scale-[0.98] mt-3 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <span className="relative flex w-full items-center justify-center rounded-[10px] bg-transparent px-4 py-3 sm:py-3.5 text-sm font-bold text-white uppercase tracking-wide">
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Enviando...
                                    </>
                                ) : (
                                    <>
                                        Enviar Instrucciones
                                        <span className="material-symbols-outlined ml-2 text-[18px]">send</span>
                                    </>
                                )}
                            </span>
                        </button>

                        {/* Back to Login */}
                        <button
                            type="button"
                            onClick={backToLogin}
                            className="flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mt-2"
                        >
                            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                            Volver a Iniciar Sesión
                        </button>
                    </form>
                ) : (
                    /* Login/Register Form */
                    <form onSubmit={isRegisterMode ? handleRegister : handleLogin} className="flex flex-col gap-4 w-full">
                        {isRegisterMode && (
                        <>
                            {/* Name Field */}
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-slate-300 uppercase tracking-wide ml-1" htmlFor="name">
                                    Tu Nombre
                                </label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-primary transition-colors">
                                        <span className="material-symbols-outlined text-[20px]">person</span>
                                    </div>
                                    <input
                                        type="text"
                                        id="name"
                                        placeholder="Juan Pérez"
                                        value={credentials.name}
                                        onChange={e => setCredentials({ ...credentials, name: e.target.value })}
                                        className="block w-full rounded-lg border-0 bg-background-dark/80 py-3.5 pl-11 pr-4 text-white shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-slate-500 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm transition-all duration-200"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Business Name Field */}
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-slate-300 uppercase tracking-wide ml-1" htmlFor="businessName">
                                    Nombre del Negocio
                                </label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-primary transition-colors">
                                        <span className="material-symbols-outlined text-[20px]">store</span>
                                    </div>
                                    <input
                                        type="text"
                                        id="businessName"
                                        placeholder="Mi Clínica Dental"
                                        value={credentials.businessName}
                                        onChange={e => setCredentials({ ...credentials, businessName: e.target.value })}
                                        className="block w-full rounded-lg border-0 bg-background-dark/80 py-3.5 pl-11 pr-4 text-white shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-slate-500 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm transition-all duration-200"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Phone Field */}
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-slate-300 uppercase tracking-wide ml-1" htmlFor="phone">
                                    WhatsApp (con código de país)
                                </label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-primary transition-colors">
                                        <span className="material-symbols-outlined text-[20px]">phone</span>
                                    </div>
                                    <input
                                        type="tel"
                                        id="phone"
                                        placeholder="18091234567"
                                        value={credentials.phone}
                                        onChange={e => setCredentials({ ...credentials, phone: e.target.value })}
                                        className="block w-full rounded-lg border-0 bg-background-dark/80 py-3.5 pl-11 pr-4 text-white shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-slate-500 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm transition-all duration-200"
                                        required
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {/* Email Field */}
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-300 uppercase tracking-wide ml-1" htmlFor="email">
                            Correo Electrónico
                        </label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-primary transition-colors">
                                <span className="material-symbols-outlined text-[20px]">mail</span>
                            </div>
                            <input
                                type="email"
                                id="email"
                                placeholder="tu@correo.com"
                                value={credentials.email}
                                onChange={e => setCredentials({ ...credentials, email: e.target.value })}
                                className="block w-full rounded-lg border-0 bg-background-dark/80 py-3.5 pl-11 pr-4 text-white shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-slate-500 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm transition-all duration-200"
                                required
                            />
                        </div>
                    </div>

                    {/* Password Field */}
                    <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-300 uppercase tracking-wide ml-1" htmlFor="password">
                            Contraseña
                        </label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-primary transition-colors">
                                <span className="material-symbols-outlined text-[20px]">lock</span>
                            </div>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                placeholder="••••••••"
                                value={credentials.password}
                                onChange={e => setCredentials({ ...credentials, password: e.target.value })}
                                className="block w-full rounded-lg border-0 bg-background-dark/80 py-3.5 pl-11 pr-12 text-white shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-slate-500 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm transition-all duration-200"
                                required
                                minLength={6}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-slate-300 transition-colors focus:outline-none"
                            >
                                <span className="material-symbols-outlined text-[20px]">
                                    {showPassword ? 'visibility_off' : 'visibility'}
                                </span>
                            </button>
                        </div>
                        {!isRegisterMode && !isForgotPasswordMode && (
                            <div className="flex justify-end pt-1">
                                <button
                                    type="button"
                                    onClick={showForgotPassword}
                                    className="text-xs font-medium text-slate-400 hover:text-white transition-colors"
                                >
                                    ¿Olvidaste tu contraseña?
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-brand-danger/10 border border-brand-danger/20 text-brand-danger text-sm">
                            <span className="material-symbols-outlined text-[18px]">error</span>
                            {error}
                        </div>
                    )}

                    {/* Success Message */}
                    {success && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-brand-accent/10 border border-brand-accent/20 text-brand-accent text-sm">
                            <span className="material-symbols-outlined text-[18px]">check_circle</span>
                            {success}
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="group relative flex w-full justify-center rounded-xl bg-gradient-to-r from-primary to-primary-light p-[1px] shadow-lg shadow-primary/25 transition-all active:scale-[0.98] mt-3 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <span className="relative flex w-full items-center justify-center rounded-[10px] bg-transparent px-4 py-3 sm:py-3.5 text-sm font-bold text-white uppercase tracking-wide">
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {isRegisterMode ? 'Creando cuenta...' : 'Verificando...'}
                                </>
                            ) : (
                                <>
                                    {isRegisterMode ? 'Crear Cuenta' : 'Iniciar Sesión'}
                                    <span className="material-symbols-outlined ml-2 text-[18px] transition-transform group-hover:translate-x-1">
                                        arrow_forward
                                    </span>
                                </>
                            )}
                        </span>
                    </button>
                    </form>
                )}

                {/* Footer / Register Toggle */}
                {!isForgotPasswordMode && (
                    <div className="flex items-center justify-center gap-2 pt-3 border-t border-white/5">
                        <span className="text-slate-400 text-sm">
                            {isRegisterMode ? '¿Ya tienes cuenta?' : '¿Nuevo en Diana?'}
                        </span>
                        <button
                            onClick={toggleMode}
                            className="text-sm font-semibold text-primary hover:text-primary transition-colors"
                        >
                            {isRegisterMode ? 'Iniciar Sesión' : 'Crear una cuenta'}
                        </button>
                    </div>
                )}
            </div>

            {/* Decorative Bottom Element */}
            <div className="mt-6 flex items-center gap-2 opacity-30">
                <span className="h-1 w-1 rounded-full bg-slate-500"></span>
                <span className="text-xs text-slate-500 font-medium">Secured by Diana IA Systems</span>
                <span className="h-1 w-1 rounded-full bg-slate-500"></span>
            </div>
        </div>
    );
};

export default Login;
